import * as React from 'react';
import { FormEvent } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { fetchUserInfoDeferred } from '../actions/Actions';
import * as CustomTypes from '../CustomTypes';
import { IState } from '../CustomTypes';
import { createB64data, shouldSkipSelectPayment } from '../fastflowUtils';
import { Address, MobileNumber } from '../input-components/InputComponents';
import {
  validateCity,
  validatePhoneNumber,
  validateZipCode,
  zipDeliverable,
} from '../validators';
import {
  trackPulsePageViewEvent,
  addParamToPulseSptCustom,
} from '../PulseUtils';
import * as queryString from 'query-string';
import {
  AlignItems,
  BackgroundColor,
  Button,
  classNames,
  Display,
  FlexDirection,
  FontSize,
  FontWeight,
  HeadingLevel,
  IconName,
  Margin,
  Padding,
  TextColor,
  Width,
} from '@snoam/pinata';
import { PageHeading } from '../layout-components/PageHeading';
import TopIcon from '../layout-components/TopIcon';

export interface IStateProps {
  productInfo: CustomTypes.IProductInfo;
  userInfo: CustomTypes.IUserInfo;
  address: CustomTypes.IAddress;
  referer: CustomTypes.IReferer;
}

interface IQueryParams {
  paymentMethodId?: string;
  purchasedSubscriptionId?: string;
}

interface IProps
  extends IStateProps,
    RouteComponentProps<Record<string, never>, Record<string, never>> {}

const styleClass = {
  button: classNames(Width.W_FULL),
  errorBlock: classNames(
    AlignItems.ITEMS_CENTER,
    BackgroundColor.BG_WHITE,
    TextColor.TEXT_NEGATIVE,
    Padding.P_3,
  ),
  form: classNames(
    Width.W_FULL,
    Margin.MT_4,
    Display.FLEX,
    FlexDirection.FLEX_COL,
  ),
  fullWidth: classNames(Width.W_FULL),
  greyBox: classNames(
    TextColor.TEXT_NEUTRAL_7,
    FontWeight.FONT_LIGHT,
    FontSize.TEXT_BASE,
    Margin.MT_2,
    Padding.PY_4,
    Display.FLEX,
    FlexDirection.FLEX_COL,
  ),
};

export class AddressPage extends React.Component<
  IProps,
  { formSubmitted: boolean; error: string }
> {
  constructor(props: IProps) {
    super(props);
    this.onSubmit = this.onSubmit.bind(this);
    this.state = {
      error: '',
      formSubmitted: false,
    };
  }

  public componentDidMount() {
    const eventName = 'Delivery address';
    fetchUserInfoDeferred.promise
      .then((user) => {
        trackPulsePageViewEvent(user, eventName);
      })
      .catch(() => {
        trackPulsePageViewEvent(this.props.userInfo, eventName);
      });
  }

  public render() {
    const query: IQueryParams = queryString.parse(
      this.props.location.search,
    ) as IQueryParams;
    const { paymentMethodId } = query;

    const buttonText =
      (paymentMethodId && 'Fullfør kjøp') ||
      this.props.productInfo === undefined ||
      this.props.productInfo.price === undefined ||
      this.props.productInfo.price === 0 ||
      this.props.productInfo.paymentMethodFree ||
      this.props.productInfo.productType === 'AVIS'
        ? 'Fortsett her'
        : 'Fortsett bestilling';

    return (
      <div className={styleClass.greyBox}>
        <TopIcon iconName={IconName.PLACE_OUTLINE} />
        <PageHeading level={HeadingLevel.ONE}>
          Gi oss din leveringsadresse
        </PageHeading>

        <form className={styleClass.form} onSubmit={this.onSubmit}>
          <div className={styleClass.fullWidth}>
            <MobileNumber
              initialFocus={true}
              validationErrorMessage={
                'Vennligst oppgi et gyldig norsk mobilnummer'
              }
            />
          </div>
          <Address
            location={this.props.location}
            history={this.props.history}
            formSubmitted={this.state.formSubmitted}
          />
          <Button
            type="submit"
            className={styleClass.button}
            text={buttonText}
            disabled={!this.isFormValid()}
          />
          {this.props.address.coAddress.length > 25 && (
            <p className={styleClass.errorBlock}>
              Navn på dør / Postkasse kan ikke være lengre enn 25 tegn.
            </p>
          )}
          {this.state.error !== '' && (
            <p className={styleClass.errorBlock}>{this.state.error}</p>
          )}
        </form>
      </div>
    );
  }

  private onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const { phoneNumber, spidId, sig, email } = this.props.userInfo;

    const query: IQueryParams = queryString.parse(
      this.props.location.search,
    ) as IQueryParams;

    const formValid = this.isFormValid();
    if (formValid) {
      this.setState({ formSubmitted: true });
      const { productId } = this.props.productInfo;
      const { paymentMethodId, purchasedSubscriptionId } = query;
      const b64data = createB64data(
        this.props.address,
        this.props.referer,
        phoneNumber,
        false,
        email,
      );
      addParamToPulseSptCustom('abo.ff.addressAdded', true);
      if (shouldSkipSelectPayment(this.props.productInfo)) {
        const params = `paymentMethodId=${paymentMethodId}&purchasedSubscriptionId=${purchasedSubscriptionId}&b64data=${encodeURIComponent(
          b64data,
        )}`;
        this.props.history.push(`/products/${productId}/placeorder?${params}`);
      } else {
        this.props.history.push(
          `/products/${productId}/selectpaymentmethod?spidId=${spidId}&sig=${sig}&b64data=${encodeURIComponent(
            b64data,
          )}`,
        );
      }
    } else {
      const { zipCode } = this.props.address;
      if (!zipDeliverable(zipCode, this.props.productInfo.validZipCodes)) {
        this.setState({
          error: 'Vi kan dessverre ikke levere til denne adressen.',
        });
      }
    }
  }

  private isFormValid() {
    const { phoneNumber } = this.props.userInfo;
    const { zipCode, city, coAddress } = this.props.address;
    if (coAddress.length > 24) {
      return false;
    }
    if (!phoneNumber || !validatePhoneNumber(phoneNumber)) {
      return false;
    }
    return (
      validateZipCode(zipCode, this.props.productInfo.validZipCodes) &&
      validateCity(city)
    );
  }
}

export default connect<
  IStateProps,
  Record<string, never>,
  Record<string, never>
>(
  (state: IState) =>
    ({
      address: state.address,
      productInfo: state.productInfo,
      referer: state.referer,
      userInfo: state.userInfo,
    }) as IStateProps,
  (dispatch) => bindActionCreators({}, dispatch),
)(withRouter(AddressPage));
